'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { cachedGetUser } from '@/app/lib/supabase';
import { courseListsApi, degreePlansApi, schedulesApi } from '@/app/services/api';
import type { AssistantMentionCandidate } from '@/app/lib/assistant-v2/mentions';

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, 160) : fallback;
}

function count(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * The student's own saved work, for the composer's `@` picker.
 *
 * Loaded lazily on the first `@` rather than on mount: most turns never mention
 * anything, and three edge-function round trips are not worth paying for on every
 * workspace open. Failures are silent — the picker simply offers less, and the
 * assistant can still reach the same records through its own list_* tools.
 */
export function useAssistantMentionCandidates() {
  const [candidates, setCandidates] = useState<AssistantMentionCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const requested = useRef(false);
  const cancelled = useRef(false);

  useEffect(() => () => { cancelled.current = true; }, []);

  const load = useCallback(async () => {
    if (requested.current) return;
    requested.current = true;
    setLoading(true);
    try {
      const user = await cachedGetUser();
      if (!user?.id) return;
      const [schedules, plans, lists] = await Promise.all([
        schedulesApi.get_all_user_schedules(user.id).catch(() => []),
        degreePlansApi.getAllUserPlans(user.id).catch(() => []),
        courseListsApi.get_all_user_course_lists(user.id).catch(() => []),
      ]);
      if (cancelled.current) return;
      const next: AssistantMentionCandidate[] = [
        ...(Array.isArray(schedules) ? schedules : []).flatMap((row: Record<string, unknown>) => {
          const id = typeof row.schedule_id === 'string' ? row.schedule_id : null;
          if (!id) return [];
          return [{
            kind: 'schedule' as const,
            id,
            label: text(row.name, 'Untitled schedule'),
            detail: [text(row.term_id, ''), plural(count(row.classes), 'course')].filter(Boolean).join(' · '),
          }];
        }),
        ...(Array.isArray(plans) ? plans : []).flatMap((row: Record<string, unknown>) => {
          if (row.id === undefined || row.id === null) return [];
          const plan = (row.plan ?? {}) as Record<string, unknown>;
          return [{
            kind: 'degree_plan' as const,
            id: String(row.id),
            label: text(plan.name ?? row.name ?? plan.major, 'Untitled plan'),
            detail: [text(plan.targetGraduation, ''), plural(count(plan.semesters), 'semester')].filter(Boolean).join(' · '),
          }];
        }),
        ...(Array.isArray(lists) ? lists : []).flatMap((row: Record<string, unknown>) => {
          if (row.id === undefined || row.id === null) return [];
          return [{
            kind: 'course_list' as const,
            id: String(row.id),
            label: text(row.name ?? row.title, 'Untitled list'),
            detail: plural(count(row.classes), 'course'),
          }];
        }),
      ];
      setCandidates(next);
    } catch {
      // A picker with nothing in it is a smaller failure than a broken composer.
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, []);

  return { candidates, loading, load };
}
